<script setup lang="ts">
import DataTable from '@/onenetwork/components/datatable/DataTable.vue'
import { onMounted, ref } from 'vue'
import TextCell from '@/onenetwork/components/datatable/TextCell.vue'
import DateTimeCell from '@/onenetwork/components/datatable/DateTimeCell.vue'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant'
import RealestateAPI from '../constants/realestate.api.uri.const'
import { formatTableDataHistory } from '../helper/table'
import Modal from '@/onenetwork/components/modal/Modal.vue'
import AssetHistoryPanel from './AssetHistoryPanel.vue'
import { useConfirm } from 'primevue'

interface Props {
  id: any
  exclude_id: any
}

const confirm = useConfirm()

const props = defineProps<Props>()

const loading = ref<boolean>(false)
const loadingDelete = ref<boolean>(false)
const pageSize = ref(10)
const page = ref(1)
const dataSource = ref({
  list: [],
  totalPage: 0,
  totalRecords: 0,
})
const displayPanel = ref(false)
const idsSelected = ref<Array<string | number>>([])

const handleChangePage = (page: number) => {
  if (page !== page.value) {
    page.value = page
    requestFetchData()
  }
}

const handleChangePageSize = (size: number) => {
  if (size !== pageSize.value) {
    pageSize.value = size
    page.value = 1 // Reset to first page when changing page size
    requestFetchData()
  }
}

const requestFetchData = async () => {
  try {
    loading.value = true
    const res = await APIClient.get(RealestateAPI.history.list, {
      params: {
        real_estate_id: props.id || null,
        exclude_id: props.exclude_id || null,
        page: page.value,
        page_size: pageSize.value,
        order_by: 'date',
        order_dir: 'desc',
      },
    })
    loading.value = false
    const { data } = res.data
    if (res.data.code === 200) {
      dataSource.value = {
        list: formatTableDataHistory(data.data) || [],
        totalPage: data.last_page,
        totalRecords: data.total,
      }
    }
  } catch (e: any) {
    console.log(e)
    loading.value = false
  }
}

const requestDelete = async () => {
  try {
    loadingDelete.value = true
    const res = await APIClient.post(RealestateAPI.history.delete, {
      ids: idsSelected.value,
    })
    loadingDelete.value = false
    if (res.data.code === 200) {
      requestFetchData()
    } else {
      console.error('Error deleting history:', res.data.message)
    }
  } catch (e: any) {
    console.error('Error deleting history:', e)
    loadingDelete.value = false
  }
}

const handleDelete = async () => {
  confirm.require({
    message: 'Bạn có muốn xoá lịch sử không?',
    header: 'Xác nhận',
    // icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Huỷ',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Đồng ý',
    },
    accept: () => {
      requestDelete()
    },
    reject: () => {
      // toast.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
    },
  })
}

const createSuccess = () => {
  displayPanel.value = false
  requestFetchData()
}

const handleChangeSelectedRow = (ids: Array<string | number>) => {
  idsSelected.value = ids
}

onMounted(() => {
  requestFetchData()
})
</script>

<template>
  <data-table
    :loading="loading"
    :buttons="[
      {
        label: 'Thêm lịch sử',
        icon: 'tabler:moneybag-plus',
        class: 'btn bg-primary bg-hover-primary text-white',
        action: () => {
          displayPanel = true
        },
      },
      {
        label: 'Xoá lịch sử',
        icon: 'tabler:moneybag-minus',
        class: `btn btn-danger bg-hover-danger`,
        action: handleDelete,
      },
    ]"
    :tableConfig="{
      canShowHide: false,
      columns: {
        code: {
          label: 'ID',
          component: TextCell,
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        date: {
          label: 'Ngày cập nhật',
          component: DateTimeCell,
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        type: {
          label: 'Trường dữ liệu',
          component: TextCell,
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        title: {
          label: 'Tiêu đề',
          component: TextCell,
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        old_value: {
          label: 'Giá trị cũ',
          component: TextCell,
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        new_value: {
          label: 'Giá trị mới',
          component: TextCell,
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
        author: {
          label: 'Thêm bởi',
          component: TextCell,
          class: '',
          visible: true,
          sort: false,
          config: {},
        },
      },
    }"
    primaryField="code"
    :page-size="pageSize"
    :page="page"
    :total-page="dataSource?.totalPage || 0"
    :total-records="dataSource?.totalRecords || 0"
    :data="dataSource?.list || []"
    @page-changed="handleChangePage"
    @pagesize-changed="handleChangePageSize"
    @on-selected-row="handleChangeSelectedRow"
  />

  <Modal
    :show="displayPanel"
    title="Thêm lịch sử"
    @on:close="
      () => {
        displayPanel = false
      }
    "
  >
    <AssetHistoryPanel :id="props.id" @update:closeModal="createSuccess" />
  </Modal>
</template>

<style scoped></style>
